// Черновите на началния екран на операция (и на таблото „Склад") — общо за
// доставка, изписване, прехвърляне и инвентаризация: моята (от сървъра, а до
// BE без черновите или при неуспешен запис — локалната в този браузър) и тези
// на колегите. Отваряне на заета чернова (друг я държи в последните 2 мин.) →
// въпросът „Поеми редакцията"; иначе листът се отваря направо и сам взима
// заключването с heartbeat. „+ Нова" при незавършена моя пита „Продължи /
// Изтрий и започни нова" (`ask`) и подава избора на листа с `{draftChoice}` —
// листът не пита втори път. Модалите са `DraftHomeModals`.
import { useMemo, useState } from "react";
import { App } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDeleteDraft, useDrafts, useTakeOverDraft } from "../../../../hooks/useInventoryDrafts";
import { lockHolderOf, type DraftType, type InventoryDraft } from "../../../../api/inventoryDrafts";
import {
  summaryOfServerDraft,
  type DraftPayloadBase,
  type DraftSummary,
  type DraftSummaryFields,
  type LocalDraftStore,
} from "./draftModel";
import type { DraftTexts } from "./draftTexts";

// Допълнителният router state на новия лист („Започни" за склад, бързото
// начало на изписването) — пази се и през въпроса за незавършената
export type NewSheetState = Record<string, unknown>;

type Params<P extends DraftPayloadBase> = {
  type: DraftType;
  newPath: string;
  local: LocalDraftStore<P>;
  // картата на локалната чернова; null — в нея няма нищо за продължаване
  localSummary: (payload: P) => DraftSummaryFields | null;
  texts: DraftTexts;
};

export const useHomeDrafts = <P extends DraftPayloadBase>({
  type,
  newPath,
  local,
  localSummary,
  texts,
}: Params<P>) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const draftsQuery = useDrafts(type);
  const takeOver = useTakeOverDraft();
  const deleteServerDraft = useDeleteDraft();
  const [localVersion, setLocalVersion] = useState(0);
  const [lockFor, setLockFor] = useState<InventoryDraft | null>(null);
  // „+ Нова" при незавършена моя — състоянието, с което листът ще тръгне
  const [askState, setAskState] = useState<NewSheetState | null>(null);

  // черновата е в localStorage — чете се наново при смяна на версията
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const localPayload = useMemo(() => local.read(), [localVersion]);

  const openServerDraft = (draft: InventoryDraft) => {
    if (draft.lockActive) {
      setLockFor(draft);
      return;
    }
    navigate(newPath, { state: { draftId: draft.id, ownDraft: draft.mine } });
  };

  const serverDrafts = draftsQuery.data ?? [];
  const serverMine = serverDrafts.find((draft) => draft.mine) ?? null;
  const localFields = !serverMine && localPayload ? localSummary(localPayload) : null;
  const mine: DraftSummary | null = serverMine
    ? summaryOfServerDraft(serverMine, () => openServerDraft(serverMine))
    : localPayload && localFields
      ? {
          ...localFields,
          key: "local",
          updatedAt: localPayload.savedAt,
          ownerName: localPayload.savedBy,
          status: "draft",
          onOpen: () => navigate(newPath, { state: { draftChoice: "continue" } }),
        }
      : null;
  const others = serverDrafts
    .filter((draft) => !draft.mine)
    .map((draft) => summaryOfServerDraft(draft, () => openServerDraft(draft)));

  // „+ Нова …" (и „Започни" за склад) — при незавършена моя първо пита
  const startNew = (state?: NewSheetState) => {
    if (mine) setAskState(state ?? {});
    else navigate(newPath, state ? { state } : undefined);
  };

  // „Изтрий и започни нова": черновата изчезва и от сървъра, и от браузъра
  const discardMineAndStartNew = (state?: NewSheetState) => {
    const proceed = () => {
      local.clear();
      setLocalVersion((version) => version + 1);
      navigate(newPath, { state: { ...state, draftChoice: "discard" } });
    };
    if (!serverMine) {
      proceed();
      return;
    }
    deleteServerDraft.mutate(serverMine.id, {
      onSuccess: proceed,
      onError: (error) => {
        const holder = lockHolderOf(error);
        if (holder != null) message.error(t("inventoryPage.homeDeleteDraftLocked", { name: holder || "?" }));
        else proceed(); // 404 — стар BE: остава само локалната
      },
    });
  };

  const confirmTakeOver = () => {
    const draft = lockFor;
    if (!draft) return;
    takeOver.mutate(draft.id, {
      onSuccess: () => {
        setLockFor(null);
        message.success(t(texts.takeOverToast, { name: draft.lockedByName ?? "" }));
        navigate(newPath, { state: { draftId: draft.id, ownDraft: draft.mine } });
      },
      onError: () => message.error(t(texts.takeOverFailed)),
    });
  };

  return {
    query: draftsQuery,
    mine,
    others,
    refreshLocal: () => setLocalVersion((version) => version + 1),
    startNew,
    ask: {
      open: askState != null,
      close: () => setAskState(null),
      discard: () => {
        const state = askState ?? undefined;
        setAskState(null);
        discardMineAndStartNew(state);
      },
    },
    lock: {
      draft: lockFor,
      busy: takeOver.isPending,
      close: () => setLockFor(null),
      confirm: confirmTakeOver,
    },
  };
};

export type HomeDrafts = ReturnType<typeof useHomeDrafts>;
